import { useCallback, useRef } from 'react';

const URL_SOUNDS = {
  win:         'https://assets.mixkit.co/active_storage/sfx/1435/1435-preview.mp3',
  click:       'https://assets.mixkit.co/active_storage/sfx/2571/2571-preview.mp3',
  score:       'https://assets.mixkit.co/active_storage/sfx/2018/2018-preview.mp3',
  achievement: 'https://assets.mixkit.co/active_storage/sfx/2019/2019-preview.mp3',
  school_good: 'https://assets.mixkit.co/active_storage/sfx/1434/1434-preview.mp3',
  school_bad:  'https://assets.mixkit.co/active_storage/sfx/2046/2046-preview.mp3',
};

export type SoundKey = keyof typeof URL_SOUNDS | 'roll' | 'clink';

// Single sharp die-landing "thock": one noise burst with a very fast decay.
// Called once per die as it settles; should be punchy but brief (~80 ms).
function synthesizeDiceLand(ctx: AudioContext) {
  const duration = 0.08;
  const rate = ctx.sampleRate;
  const samples = Math.floor(rate * duration);
  const buffer = ctx.createBuffer(1, samples, rate);
  const data = buffer.getChannelData(0);

  for (let i = 0; i < samples; i++) {
    // Decay constant ≈ 8 ms → nearly silent by 30 ms
    data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (rate * 0.008));
  }

  const src = ctx.createBufferSource();
  src.buffer = buffer;

  // Brighter than the roll (1600–2300 Hz) and more resonant — gives a crisp "tick"
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 1600 + Math.random() * 700;
  bp.Q.value = 1.8;

  const gain = ctx.createGain();
  gain.gain.value = 5.0;

  src.connect(bp);
  bp.connect(gain);
  gain.connect(ctx.destination);
  src.start();
}

// Synthesise a dice-clatter: several noise bursts spaced exponentially
// (dense at start = dice tumbling fast, sparse at end = dice slowing down).
// Each call is randomised so no two rolls sound identical.
function synthesizeDiceRoll(ctx: AudioContext) {
  const duration = 0.7;
  const rate = ctx.sampleRate;
  const buffer = ctx.createBuffer(1, Math.floor(rate * duration), rate);
  const data = buffer.getChannelData(0);

  const impacts = 7 + Math.floor(Math.random() * 5); // 7–11 impacts

  for (let k = 0; k < impacts; k++) {
    // Exponential distribution: most energy in the first half
    const t = Math.pow(k / impacts, 1.7);
    const startSample = Math.floor(t * buffer.length * 0.90);
    // Burst gets shorter as dice slow down
    const burstSamples = Math.floor(rate * (0.020 - t * 0.011));
    for (let i = 0; i < burstSamples && startSample + i < buffer.length; i++) {
      const env = Math.exp(-i / (burstSamples * 0.30));
      data[startSample + i] += (Math.random() * 2 - 1) * env * (1 - t * 0.55);
    }
  }

  const src = ctx.createBufferSource();
  src.buffer = buffer;

  // Bandpass tuned to the "hard plastic on wood" frequency range
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 1000 + Math.random() * 500; // 1000–1500 Hz varies per roll
  bp.Q.value = 0.6;

  const gain = ctx.createGain();
  gain.gain.value = 4.0;

  src.connect(bp);
  bp.connect(gain);
  gain.connect(ctx.destination);
  src.start();
}

export function useSound(enabled: boolean = true) {
  const audioCache = useRef<Record<string, HTMLAudioElement>>({});
  const audioCtxRef = useRef<AudioContext | null>(null);

  const play = useCallback((key: SoundKey) => {
    if (!enabled) return;

    if (key === 'roll' || key === 'clink') {
      try {
        if (!audioCtxRef.current) {
          audioCtxRef.current = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
        }
        const ctx = audioCtxRef.current;
        const fn = key === 'roll' ? synthesizeDiceRoll : synthesizeDiceLand;
        // iOS suspends AudioContext until a user gesture; resume then play.
        if (ctx.state === 'suspended') {
          ctx.resume().then(() => fn(ctx)).catch(() => {});
        } else {
          fn(ctx);
        }
      } catch (e) {
        console.warn('Web Audio error:', e);
      }
      return;
    }

    try {
      if (!audioCache.current[key]) {
        audioCache.current[key] = new Audio(URL_SOUNDS[key]);
      }
      const audio = audioCache.current[key];
      audio.currentTime = 0;
      audio.play().catch(e => console.warn('Audio play failed:', e));
    } catch (e) {
      console.warn('Audio error:', e);
    }
  }, [enabled]);

  return { play };
}
